import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CalendarDays, Clock, MapPin, Users, Minus, Plus, BadgeCheck, Share2,
  Shirt, Music, Car, Accessibility, Undo2, Sparkles, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DetailGallery } from "@/components/DetailGallery";
import { Rating } from "@/components/Rating";
import { ReviewsSection } from "@/components/ReviewsSection";
import { useLanguage } from "@/i18n/LanguageContext";

const fullDate = (iso, lang) =>
  new Intl.DateTimeFormat(lang === "en" ? "en-CA" : "fr-CA", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(
    new Date(`${iso}T12:00:00`)
  );

const TABS = [
  { key: "apropos", label: "À propos" },
  { key: "details", label: "Détails" },
  { key: "artistes", label: "Artistes / DJ" },
  { key: "infos", label: "Infos pratiques" },
  { key: "avis", label: "Avis" },
];

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [event, setEvent] = useState(null);
  const [spots, setSpots] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("apropos");
  const [guests, setGuests] = useState(2);
  const [reserved, setReserved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEvent(null);
    setNotFound(false);
    setReserved(false);
    api
      .get(`/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setNotFound(true));
    api.get("/establishments").then((r) => setSpots(r.data)).catch(() => {});
    api.post("/views", { item_type: "evenement", item_slug: slug }).catch(() => {});
  }, [slug]);

  const venueSpot = useMemo(() => {
    if (!event) return null;
    const v = (event.venue || "").toLowerCase();
    return (
      spots.find(
        (s) => s.name.toLowerCase() === v || v.includes(s.name.toLowerCase()) || s.name.toLowerCase().includes(v)
      ) || null
    );
  }, [event, spots]);

  if (notFound) {
    return (
      <main className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="event-not-found">
        <h1 className="text-2xl font-bold text-slate-900">{t("Événement introuvable")}</h1>
        <Link to="/evenements" data-testid="back-events-link" className="mt-6 inline-block rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white">
          {t("Voir les événements")}
        </Link>
      </main>
    );
  }

  if (!event) {
    return (
      <main className="mx-auto max-w-[1800px] px-4 py-10 sm:px-6">
        <div className="h-96 animate-pulse rounded-3xl bg-white" />
      </main>
    );
  }

  const addToCalendar = () => {
    const hhmm = (event.time_label || "20h00").replace("h", "").padEnd(4, "0");
    const d = (event.date || "").replaceAll("-", "");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `UID:${event.slug}@gospot`,
      `DTSTART:${d}T${hhmm}00`,
      `SUMMARY:${event.title}`,
      `LOCATION:${event.venue} — ${event.address || "Québec, QC"}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    a.download = `${event.slug}.ics`;
    a.click();
    toast.success(t("Événement ajouté à ton calendrier"));
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: event.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t("Lien copié !"));
      }
    } catch {
      /* annulé */
    }
  };

  const reserve = async () => {
    if (!user) {
      navigate("/connexion");
      return;
    }
    setBusy(true);
    try {
      if (event.price > 0) {
        const { data } = await api.post("/payments/checkout", {
          event_slug: event.slug,
          guests,
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/reservations", { event_slug: event.slug, guests });
      setReserved(true);
      toast.success(
        lang === "en"
          ? `Reservation confirmed for ${guests} ${guests > 1 ? "people" : "person"} — confirmation email sent`
          : `Réservation confirmée pour ${guests} personne${guests > 1 ? "s" : ""} — courriel de confirmation envoyé`
      );
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  const ki = event.key_info || {};

  return (
    <main data-testid="event-detail-page" className="mx-auto max-w-[1800px] px-4 pb-16 pt-6 sm:px-6 lg:px-10">
      <Seo title={`${event.title} — GoSpot`} siteName="GoSpot" description={event.description || `${event.title} à ${event.venue}, Québec.`} />

      <nav className="mb-5 flex items-center gap-1.5 text-sm text-slate-400" data-testid="breadcrumb">
        <Link to="/" className="transition hover:text-[#2563EB]">{t("Accueil")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/evenements" className="transition hover:text-[#2563EB]">{t("Événements")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="truncate font-medium text-slate-700">{event.title}</span>
      </nav>

      <DetailGallery images={event.gallery} badge={event.badge} item={event} itemType="evenement" />

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="min-w-0">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{event.title}</h1>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {venueSpot && (
                <img src={venueSpot.image} alt={event.venue} className="h-12 w-12 rounded-full object-cover" />
              )}
              <div>
                <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                  {event.venue}
                  <BadgeCheck className="h-4 w-4 fill-[#2563EB] text-white" />
                </p>
                <p className="text-sm text-slate-500">
                  {t(event.category_label)} • {event.neighborhood}, Québec
                </p>
                {venueSpot && (
                  <div className="mt-0.5">
                    <Rating value={venueSpot.rating} count={venueSpot.reviews_count} />
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              data-testid="share-button"
              onClick={share}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-[#2563EB]"
            >
              <Share2 className="h-4 w-4" />
              {t("Partager")}
            </button>
          </div>

          {/* ONGLETS */}
          <div className="no-scrollbar mt-8 flex gap-6 overflow-x-auto border-b border-slate-200">
            {TABS.map((tabItem) => (
              <button
                key={tabItem.key}
                type="button"
                data-testid={`tab-${tabItem.key}`}
                onClick={() => setTab(tabItem.key)}
                className={`whitespace-nowrap border-b-2 pb-3 text-sm font-semibold transition ${
                  tab === tabItem.key ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {t(tabItem.label)}
                {tabItem.key === "avis" && venueSpot ? ` (${venueSpot.reviews_count})` : ""}
              </button>
            ))}
          </div>

          <div className="mt-6">
            {tab === "apropos" && (
              <div data-testid="tab-content-apropos">
                <h2 className="text-lg font-bold text-slate-900">{t("À propos de l’événement")}</h2>
                <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-600">{event.description}</p>
                {event.tags?.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    {event.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-[#EFF6FF] px-4 py-1.5 text-xs font-semibold text-[#2563EB]">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {tab === "details" && (
              <div data-testid="tab-content-details" className="grid gap-3 sm:grid-cols-2">
                {[
                  { icon: CalendarDays, label: "Date", value: fullDate(event.date, lang) },
                  { icon: Clock, label: "Heure", value: `${event.time_label} – ${event.end_time || "23h00"}` },
                  { icon: MapPin, label: "Quartier", value: event.neighborhood },
                  { icon: Sparkles, label: "Catégorie", value: event.category_label },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                    <row.icon className="h-5 w-5 text-[#2563EB]" />
                    <div>
                      <p className="text-xs text-slate-400">{t(row.label)}</p>
                      <p className="text-sm font-semibold text-slate-900">{row.label === "Catégorie" ? t(row.value) : row.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === "artistes" && (
              <div data-testid="tab-content-artistes">
                {event.artists?.length ? (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {event.artists.map((a) => (
                      <div key={a.name} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3">
                        <img src={a.image} alt={a.name} className="h-12 w-12 rounded-full object-cover" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{a.name}</p>
                          <p className="text-xs text-slate-400">{a.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-2xl bg-white p-6 text-sm text-slate-500">{t("La programmation sera annoncée bientôt.")}</p>
                )}
              </div>
            )}
            {tab === "infos" && (
              <div data-testid="tab-content-infos" className="rounded-3xl border border-slate-100 bg-white p-5">
                {[
                  { icon: Users, label: "Âge minimum", value: ki.age },
                  { icon: Shirt, label: "Tenue vestimentaire", value: ki.dress_code },
                  { icon: Music, label: "Musique", value: ki.music },
                  { icon: Car, label: "Stationnement", value: ki.parking },
                  { icon: Accessibility, label: "Accessibilité", value: ki.accessibility },
                  { icon: Undo2, label: "Remboursement", value: ki.refund },
                ]
                  .filter((r) => r.value)
                  .map((row) => (
                    <div key={row.label} className="flex items-center justify-between border-b border-slate-50 py-3 text-sm last:border-0">
                      <span className="flex items-center gap-2.5 text-slate-500">
                        <row.icon className="h-4 w-4 text-slate-400" />
                        {t(row.label)}
                      </span>
                      <span className="font-semibold text-slate-900">{row.value}</span>
                    </div>
                  ))}
              </div>
            )}
            {tab === "avis" && (
              <div data-testid="tab-content-avis">
                <ReviewsSection itemType="evenement" itemSlug={event.slug} />
              </div>
            )}
          </div>
        </div>

        {/* COLONNE DROITE */}
        <aside className="space-y-5 self-start lg:sticky lg:top-24">
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="date-card">
            <p className="flex items-center gap-2.5 text-sm font-bold text-slate-900">
              <CalendarDays className="h-5 w-5 text-[#2563EB]" />
              {fullDate(event.date, lang)}
            </p>
            <p className="mt-3 flex items-center gap-2.5 text-sm font-semibold text-slate-700">
              <Clock className="h-5 w-5 text-[#2563EB]" />
              {event.time_label} – {event.end_time || "23h00"}
            </p>
            <button
              type="button"
              data-testid="add-calendar-button"
              onClick={addToCalendar}
              className="mt-2 pl-7 text-sm font-medium text-[#2563EB] transition hover:underline"
            >
              {t("Ajoutez à votre calendrier")}
            </button>
          </div>

          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="venue-card">
            <p className="flex items-center gap-2.5 text-sm font-bold text-slate-900">
              <MapPin className="h-5 w-5 text-[#2563EB]" />
              {event.venue}
            </p>
            <p className="mt-2 pl-7 text-sm text-slate-500">{event.address || `${event.neighborhood}, Québec, QC`}</p>
            <Link
              to="/explorer?vue=carte"
              data-testid="see-on-map-link"
              className="mt-1.5 inline-block pl-7 text-sm font-medium text-[#2563EB] transition hover:underline"
            >
              {t("Voir sur la carte")}
            </Link>
          </div>

          <div id="reserver" className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="reservation-card">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">{t("Réserver votre place")}</h2>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Users className="h-3.5 w-3.5" />
                {t("Places limitées")}
              </span>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-slate-600">{t("Nombre de personnes")}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="guests-minus"
                  onClick={() => setGuests((g) => Math.max(1, g - 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-blue-300"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span data-testid="guests-count" className="w-5 text-center text-sm font-bold">{guests}</span>
                <button
                  type="button"
                  data-testid="guests-plus"
                  onClick={() => setGuests((g) => Math.min(20, g + 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-blue-300"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <button
              type="button"
              data-testid="reserve-button"
              onClick={reserve}
              disabled={busy || reserved}
              className={`mt-5 w-full rounded-full py-3.5 text-sm font-semibold text-white transition active:scale-[0.98] ${
                reserved ? "cursor-default bg-emerald-500" : "bg-[#2563EB] hover:bg-[#1D4ED8]"
              } disabled:opacity-80`}
            >
              {busy
                ? t("Réservation…")
                : reserved
                  ? t("Place réservée ✓")
                  : event.price > 0
                    ? lang === "en" ? `Book — $${event.price * guests}` : `Réserver — ${event.price * guests} $`
                    : t("Réserver maintenant")}
            </button>
            <p className="mt-3 text-center text-xs text-slate-400">
              {event.price > 0
                ? lang === "en" ? `$${event.price} per person • ` : `${event.price} $ par personne • `
                : `${t("Gratuit")} • `}
              {t("Confirmation instantanée")}
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}
